import { z } from 'zod';
import { IdSchema } from './common.js';
import { PERMISSION_KEYS, ScopeLevelSchema } from './permissions.js';

/**
 * Roles as people manage them (docs/33 §3): the built-in ones, read-only, and custom ones owned by
 * the platform, a reseller or a client.
 */

export const ROLE_OWNERS = ['builtin', 'platform', 'reseller', 'tenant'] as const;
export const RoleOwnerSchema = z.enum(ROLE_OWNERS);
export type RoleOwner = z.infer<typeof RoleOwnerSchema>;

export const RoleSchema = z.object({
  id: IdSchema,
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  scope_level: ScopeLevelSchema,
  owner: RoleOwnerSchema,
  reseller_id: IdSchema.nullable(),
  tenant_id: IdSchema.nullable(),
  permissions: z.array(z.string()),
  /** People who hold it, within what the caller can see. */
  holders: z.number().int(),
  /** Whether the caller may change it: custom, theirs to manage, and nothing in it they lack. */
  editable: z.boolean(),
  /** Whether the caller may hand it to somebody: they hold every permission in it. */
  grantable: z.boolean(),
});
export type Role = z.infer<typeof RoleSchema>;

const ROLE_NAME = z.string().trim().min(1).max(60).regex(/^[^\r\n]*$/, 'must be one line');
const ROLE_PERMISSIONS = z.array(z.enum(PERMISSION_KEYS as [string, ...string[]])).max(200)
  .transform((keys) => [...new Set(keys)].sort());

/** A platform or reseller role (POST /roles). A client's own roles use RoleCreateForTenantSchema. */
export const RoleCreateSchema = z.object({
  name: ROLE_NAME,
  description: z.string().trim().max(200).nullable().default(null),
  scope_level: z.enum(['platform', 'reseller', 'tenant']),
  /** The reseller that owns it; left out for a platform role. */
  reseller_id: IdSchema.nullable().default(null),
  permissions: ROLE_PERMISSIONS,
}).strict();
export type RoleCreate = z.infer<typeof RoleCreateSchema>;

/** A client's own role (POST /tenants/:t/roles): always client level. */
export const RoleCreateForTenantSchema = z.object({
  name: ROLE_NAME,
  description: z.string().trim().max(200).nullable().default(null),
  permissions: ROLE_PERMISSIONS,
}).strict();
export type RoleCreateForTenant = z.infer<typeof RoleCreateForTenantSchema>;

export const RoleUpdateSchema = z.object({
  name: ROLE_NAME,
  description: z.string().trim().max(200).nullable().default(null),
  permissions: ROLE_PERMISSIONS,
}).strict();
export type RoleUpdate = z.infer<typeof RoleUpdateSchema>;

/** Changing somebody's role in place (PUT /memberships/:id). */
export const MembershipUpdateSchema = z.object({
  role_id: IdSchema,
}).strict();
export type MembershipUpdate = z.infer<typeof MembershipUpdateSchema>;
